import * as React from 'react';
import { CopyableField } from '../CopyableField/CopyableField';
import Button from '@app/react-common/components/button/Button';
import { getButtonText } from '../../shared/abTestModalUtils';
import { Shop } from '@pdqdev/pdq-shared/dist/entities/Shop';
import { environment } from '../../../../../../../../../../../../environments/environment';
import { WarningBanner } from '@app/react-common/components/banners/WarningBanner';

const getThemeAbTestScript = () => {
  const scriptsCdnUrl = environment.SCRIPTS_CDN_URL;
  return `{%- assign theme_test_config = shop.metafields.custom.theme_test_config.value -%}
<style id="pdq-antiflicker">html,body{opacity:0!important;visibility:hidden!important}</style>
<script>window.pdqThemeTestConfig={{ theme_test_config | default: 'null' | json }};</script>
<script>"use strict";!function(){var e,t;try{if(window.self!==window.top){const e=document.getElementById("pdq-antiflicker");return void(e&&e.remove())}const o=new URL(window.location.href);if(o.searchParams.get("variant"))return;const n=sessionStorage.getItem("pdq_redirect_in_progress");if(n){const e=parseInt(n,10);if(Date.now()-e<3e3){const e=document.getElementById("pdq-antiflicker");return void(e&&e.remove())}sessionStorage.removeItem("pdq_redirect_in_progress")}const r=localStorage.getItem("pdq_theme_session");if(!r)return;const i=JSON.parse(r),s=Date.now();if(i.expiry&&s>i.expiry)return void localStorage.removeItem("pdq_theme_session");const d=window.pdqThemeTestConfig;if((null==d?void 0:d.abTestToken)&&i.abTestToken!==d.abTestToken)return;const c=null==(t=null==(e=null==window?void 0:window.Shopify)?void 0:e.theme)?void 0:t.id,a=i.themeId;if(c&&a&&c!==a)return;sessionStorage.removeItem("pdq_redirect_in_progress");const m=document.getElementById("pdq-antiflicker");m&&m.remove()}catch(e){const t=document.getElementById("pdq-antiflicker");t&&t.remove()}}();</script>
<script>window.pdqFailsafeTimeoutId=setTimeout(function(){var f=sessionStorage.getItem("pdq_redirect_in_progress");console.log("[PDQ Failsafe] Checking redirect flag:",f);if(!f){var s=document.getElementById("pdq-antiflicker");s&&(s.remove(),console.warn("[PDQ Theme AB Test] ⏰ INLINE FAILSAFE TIMEOUT (3000ms) - Showing page"))}else{console.log("[PDQ Failsafe] Redirect in progress, keeping anti-flicker active")}},3000);</script>
<script type="text/javascript" id="pdq_theme_ab_test_script" src="${scriptsCdnUrl}/pdq-theme-abtest.min.js"></script>`;
};

const THEME_AB_TEST_SCRIPT = getThemeAbTestScript();

interface ThemeScriptTabProps {
  mode: string;
  getPreviousTab: (currentTabName: string) => string | null;
  getNextTab: (currentTabName: string) => string | null;
  setActiveTab: (tab: string) => void;
  onClose: () => void;
  handleSave: () => Promise<void>;
  shop: Shop | null;
}

export default function ThemeScriptTab({
  mode,
  getPreviousTab,
  getNextTab,
  setActiveTab,
  onClose,
  handleSave,
  shop,
}: ThemeScriptTabProps) {
  const handleBackClick = () => {
    const prevTab = getPreviousTab('Installation');
    if (prevTab) {
      setActiveTab(prevTab);
    }
  };

  const nextTab = getNextTab('Installation');

  const handleNextOrSaveClick = async () => {
    if (mode === 'view') {
      nextTab ? setActiveTab(nextTab) : onClose();
    } else {
      nextTab ? setActiveTab(nextTab) : await handleSave();
    }
  };

  const buttonText = nextTab ? 'Next' : getButtonText(mode);

  return (
    <>
      <div className="theme-script-tab">
        <div className="theme-script-tab__header-section">
          <WarningBanner
            title="Warning"
            content="Do not delete themes used in test from Shopify, deleting the theme can break the storefront."
          />
          <h3 className="theme-script-tab__heading">
            <strong>Installation</strong>{' '}
            <span className="theme-script-tab__heading-subtitle">(One-time per theme)</span>
          </h3>

          <p className="theme-script-tab__description">
            To activate Cross-Site A/B testing, add the script to each Shopify theme you want to test.
          </p>
        </div>

        <div className="theme-script-tab__script-section">
          <CopyableField value="Cross-Site A/B test script" copyValue={THEME_AB_TEST_SCRIPT} />
        </div>

        <h4 className="theme-script-tab__steps-heading">Steps:</h4>

        <div className="theme-script-tab__steps">
          <div className="theme-script-tab__step">
            <div className="theme-script-tab__step-number">1</div>
            <div className="theme-script-tab__step-content">
              <h5 className="theme-script-tab__step-title">Go to Shopify Admin</h5>
              <p className="theme-script-tab__step-description">Navigate to Online Store → Themes</p>
            </div>
          </div>

          <div className="theme-script-tab__step">
            <div className="theme-script-tab__step-number">2</div>
            <div className="theme-script-tab__step-content">
              <h5 className="theme-script-tab__step-title">Open the theme's code</h5>
              <p className="theme-script-tab__step-description">For each theme you want to test, click Edit code</p>
            </div>
          </div>

          <div className="theme-script-tab__step">
            <div className="theme-script-tab__step-number">3</div>
            <div className="theme-script-tab__step-content">
              <h5 className="theme-script-tab__step-title">Open theme.liquid</h5>
              <p className="theme-script-tab__step-description">
                In the left sidebar, open Layout → <code>theme.liquid</code>
              </p>
            </div>
          </div>

          <div className="theme-script-tab__step">
            <div className="theme-script-tab__step-number">4</div>
            <div className="theme-script-tab__step-content">
              <h5 className="theme-script-tab__step-title">Paste the script</h5>
              <p className="theme-script-tab__step-description">
                Add it right after the opening <code>&lt;head&gt;</code> tag
              </p>
            </div>
          </div>

          <div className="theme-script-tab__step">
            <div className="theme-script-tab__step-number">5</div>
            <div className="theme-script-tab__step-content">
              <h5 className="theme-script-tab__step-title">Save the file</h5>
              <p className="theme-script-tab__step-description">Click Save to apply changes</p>
            </div>
          </div>
        </div>

        <div className="theme-script-tab__footer-item">
          <i className="icon icon-more_information theme-script-tab__footer-item-icon"></i>
          <p className="theme-script-tab__footer-item-text">
            One-time setup per theme — no need to repeat for future tests.
          </p>
        </div>

        <p className="theme-script-tab__footer-note">
          After setup, the test will be created as a Draft. You can start it anytime from the Drafts view.
        </p>
      </div>
      <div className="ab-test-modal__actions">
        <Button
          onClick={handleBackClick}
          text={'Back'}
          size={'medium'}
          variant={'secondary'}
          className="ab-test-modal__actions__cancel"
        />
        <Button onClick={handleNextOrSaveClick} text={buttonText} size={'medium'} variant={'primary'} />
      </div>
    </>
  );
}
